import { Loading, Menu, MenuItem, MenuSeparator } from '@affine/component';
import { AuthService, DefaultServerService } from '@affine/core/modules/cloud';
import { GlobalDialogService } from '@affine/core/modules/dialogs';
import { ServerFeature } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { PlusIcon, SettingsIcon, TemplateIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { type ReactNode, useCallback, useState } from 'react';

import * as styles from './index.css';
import {
  type CreatedWorkspacePayload,
  useCreateWorkspaceFromTemplate,
} from './use-create-workspace';
import {
  useWorkspaceTemplates,
  type WorkspaceTemplateOption,
} from './use-workspace-templates';

export {
  type CreatedWorkspacePayload,
  useCreateWorkspaceFromTemplate,
} from './use-create-workspace';
export {
  useWorkspaceTemplates,
  type WorkspaceTemplateOption,
} from './use-workspace-templates';

/**
 * The template picker itself. Mount it only inside an open menu — the hook it
 * uses opens every workspace to read its folder-template flags.
 */
export const CreateWorkspaceMenuItems = ({
  onSelectTemplate,
  onMoreOptions,
}: {
  onSelectTemplate: (template: WorkspaceTemplateOption | null) => void;
  onMoreOptions?: () => void;
}) => {
  const t = useI18n();
  const { options, loading } = useWorkspaceTemplates();

  return (
    <>
      <MenuItem
        prefixIcon={<PlusIcon />}
        data-testid="create-workspace-template-empty"
        onSelect={() => onSelectTemplate(null)}
      >
        {t['com.affine.nameWorkspace.template.empty']()}
      </MenuItem>
      {loading ? (
        <div className={styles.hint}>
          <Loading size={16} speed={0} />
          {t['com.affine.nameWorkspace.template.loading']()}
        </div>
      ) : options.length === 0 ? (
        <div className={styles.hint}>
          {t['com.affine.nameWorkspace.template.none']()}
        </div>
      ) : (
        options.map(option => (
          <MenuItem
            key={option.key}
            prefixIcon={<TemplateIcon />}
            data-testid={`create-workspace-template-${option.folderId}`}
            onSelect={() => onSelectTemplate(option)}
          >
            <div className={styles.titles}>
              <span className={styles.title}>{option.folderName}</span>
              <span className={styles.subtitle}>{option.workspaceName}</span>
            </div>
          </MenuItem>
        ))
      )}
      {onMoreOptions ? (
        <>
          <MenuSeparator />
          <MenuItem
            prefixIcon={<SettingsIcon />}
            data-testid="create-workspace-more-options"
            onSelect={onMoreOptions}
          >
            {t['com.affine.nameWorkspace.template.more-options']()}
          </MenuItem>
        </>
      ) : null}
    </>
  );
};

/**
 * Returns the guard used before showing the picker: creating a workspace still
 * requires an account unless local workspaces are enabled.
 */
export const useCreateWorkspaceGuard = () => {
  const globalDialogService = useService(GlobalDialogService);
  const defaultServerService = useService(DefaultServerService);
  const session = useLiveData(useService(AuthService).session.session$);
  const isAuthenticated = session.status === 'authenticated';

  return useCallback(() => {
    // config$ is null until the server config loads; reading `.features`
    // unguarded throws and silently swallows the click.
    const config = defaultServerService.server.config$.value;
    if (!config) {
      defaultServerService.server.revalidateConfig();
    }
    const enableLocalWorkspace =
      BUILD_CONFIG.isNative ||
      (config?.features.includes(ServerFeature.LocalWorkspace) ?? false);
    if (!isAuthenticated && !enableLocalWorkspace) {
      globalDialogService.open('sign-in', {});
      return false;
    }
    return true;
  }, [defaultServerService, globalDialogService, isAuthenticated]);
};

/**
 * Wraps a trigger so that clicking it drops a template list instead of opening
 * the create-workspace dialog; picking an entry creates the workspace right
 * away.
 */
export const CreateWorkspaceMenu = ({
  children,
  onCreated,
  onEventEnd,
}: {
  children: ReactNode | ((state: { creating: boolean }) => ReactNode);
  onCreated?: (payload: CreatedWorkspacePayload) => void;
  onEventEnd?: () => void;
}) => {
  const globalDialogService = useService(GlobalDialogService);
  const [open, setOpen] = useState(false);
  const checkCanCreate = useCreateWorkspaceGuard();
  const { creating, createWorkspace } =
    useCreateWorkspaceFromTemplate(onCreated);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setOpen(false);
        return;
      }
      if (!checkCanCreate()) return;
      setOpen(true);
    },
    [checkCanCreate]
  );

  const handleSelectTemplate = useCallback(
    (template: WorkspaceTemplateOption | null) => {
      createWorkspace(template);
      onEventEnd?.();
    },
    [createWorkspace, onEventEnd]
  );

  const handleMoreOptions = useCallback(() => {
    globalDialogService.open('create-workspace', {}, payload => {
      if (payload) onCreated?.(payload);
    });
    onEventEnd?.();
  }, [globalDialogService, onCreated, onEventEnd]);

  return (
    <Menu
      rootOptions={{ open, onOpenChange: handleOpenChange }}
      contentOptions={{ className: styles.menuContent, align: 'start' }}
      items={
        <CreateWorkspaceMenuItems
          onSelectTemplate={handleSelectTemplate}
          onMoreOptions={handleMoreOptions}
        />
      }
    >
      {typeof children === 'function' ? children({ creating }) : children}
    </Menu>
  );
};
